import { useMemo } from "react";

import type { BoardColumn } from "~/shared/types/quote-board-types.ts";
import type { GridPrefColumn } from "~/shared/utils/apply-grid-prefs.ts";
import { applyGridPrefs } from "~/shared/utils/apply-grid-prefs.ts";
import { getDefaultShipmentDetailReportGridPrefs } from "~/shared/utils/default-grid-prefs.ts";
import { GridIdentifiers } from "~/shared/utils/grid-identifier-enums.ts";

import { SHIPMENT_DETAIL_COLUMNS } from "../constants/shipment-detail-columns.tsx";

type UseShipmentDetailReportColumnsArgs = {
	/** The user's saved Shipment Details Report column prefs (external users only). */
	columnPrefs?: GridPrefColumn[];
};

/**
 * Builds the Shipment Details Report grid columns by merging the code-defined
 * defaults with the user's persisted column preferences (visibility, order,
 * width).
 */
export const useShipmentDetailReportColumns = ({
	columnPrefs,
}: UseShipmentDetailReportColumnsArgs): BoardColumn[] => {
	return useMemo(() => {
		const defaults =
			getDefaultShipmentDetailReportGridPrefs(false)[
				GridIdentifiers["Shipment Details Report"]
			]?.columns ?? [];
		const defaultsByField = new Map(
			defaults.map((pref) => [pref.columnId, pref]),
		);
		const savedByField = new Map(
			(columnPrefs ?? []).map((pref) => [pref.columnId, pref]),
		);

		// Merge defaults with the user's saved prefs. `isVisible`, `widthSize`,
		// and `ordinalPosition` are user preferences and the saved value wins.
		// `visibility` is column metadata (which deployments may see a column at
		// all) — defaults always win so updates to a column's deployment
		// visibility take effect for users with stale persisted prefs.
		const mergedById = new Map<string, GridPrefColumn>();
		for (const def of defaults) {
			const saved = savedByField.get(def.columnId);
			mergedById.set(
				def.columnId,
				saved ? { ...saved, visibility: def.visibility } : def,
			);
		}
		// Preserve any saved entries not present in defaults (forward compat).
		for (const saved of savedByField.values()) {
			if (!defaultsByField.has(saved.columnId)) {
				mergedById.set(saved.columnId, saved);
			}
		}
		const mergedPrefs = [...mergedById.values()];

		return applyGridPrefs(SHIPMENT_DETAIL_COLUMNS, mergedPrefs, false);
	}, [columnPrefs]);
};
